// release-deploy-outcome-manual-resolution-types.ts
//
// Input/result shapes of resolve-deploy (release-deploy-outcome-manual-
// resolution.ts) and the one place a refusal is built. Moved out verbatim to
// keep that file under the 200-line budget once M4 (retracting a manual
// `deployed`) landed; it re-exports them, so importers are unchanged.
import type { LockLivenessProbe } from "./process-liveness-probe.js";

export type ManualDeployAnswer = "deployed" | "not-deployed";

export interface ResolveUnknownDeployInput {
  controlRoot: string;
  missionId: string;
  candidateSha: string;
  outcome: ManualDeployAnswer;
  actorId: string;
  lockPath: string;
  liveness: Pick<LockLivenessProbe, "isAlive">;
  /**
   * Deploy hash of the CURRENT config.release for `candidateSha`
   * (computeReleaseDeployHash); `undefined` when no release config is loaded.
   * `deployed` requires the unknown deploy to match it.
   */
  currentDeployHash: string | undefined;
  now?: () => Date;
}

export type ResolveUnknownDeployResult =
  | {
      ok: true;
      mission_id: string;
      candidate_sha: string;
      outcome: ManualDeployAnswer;
      resolved_operation_ids: string[];
      /** An earlier manual `deployed` answer was taken back (review P2 M4). */
      retracted_manual_deployed?: true;
      /** The deploy receipt that answer had produced was removed (review 261001 M2); false when none existed or one stayed. */
      deploy_receipt_removed: boolean;
      /** A deploy receipt that stays and decides the next `run`: `real` (a deploy wrote it) or `damaged`. */
      blocking_deploy_receipt?: "real" | "damaged";
    }
  | {
      ok: false;
      reason:
        | "candidate_invalid"
        | "actor_not_bound"
        | "lock_held_by_this_mission"
        | "journal_busy"
        | "journal_recovering"
        | "nothing_unknown"
        | "candidate_mismatch"
        | "deploy_hash_mismatch"
        | "smoke_already_passed"
        | "manual_answer_not_in_force";
      message: string;
    };

export function refuse(
  reason: Extract<ResolveUnknownDeployResult, { ok: false }>["reason"],
  message: string,
): ResolveUnknownDeployResult {
  return { ok: false, reason, message };
}
